import { Box, Flex, useClipboard } from '@chakra-ui/react';
import { Icon } from '@foam/buoy/src/components/Icon/Icon';
import { toast } from '@foam/buoy/src/components/Toast/Toast';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalBody,
  ModalHeaderDefault,
  useSnackbar,
} from 'ui-components';
import { ContentCaption } from '~/components/ContentCaption';
import {
  Actions,
  Asset,
  AssetDescription,
  CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
  CONTENT_DETAIL_MODAL_TRACK_ID,
  ContentDetailModalTrackIdType,
  Header,
} from '~/components/ContentDetailModal';
import { NewConfirmationModal } from '~/components/NewConfirmationModal';
import {
  FEATURE_FLAGS,
  INSTAGRAM_CONTENT_TYPES,
  SOCIAL_PLATFORMS,
} from '~/constants';
import { trackEvent } from '~/core/utils/trackEvent';
import { useGetContent } from '~/hooks/api/useGetContent';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { TalentContentProperties } from '~/screens/PageBuilder/blocks/TalentContent/TalentContent';
import { usePageBuilderStore } from '~/screens/PageBuilder/hooks/usePageBuilderStore';
import { AnyBlock } from '~/screens/PageBuilder/types';
import type {
  IApiSearchInstagramContent,
  ISearchContentCreator,
} from '~/searchApi';

import { ContentDetailMetrics } from '../ContentDetailMetrics';
import { PLATFORM_METRICS } from '../ContentDetailMetrics/ContentDetailMetrics.constants';

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  onNavigation?: (direction: string) => void;
  trackIds?: ContentDetailModalTrackIdType;
  id?: string;
  shareToken?: string;
}

export const ContentDetailModal = ({
  isOpen,
  onClose,
  onNavigation,
  trackIds: overrideDefaultTrackIds,
  id,
  shareToken,
}: Props) => {
  const router = useRouter();
  // If there's id (id of the content) use it, if not get it from the query
  const contentId = id ? id : (router.query.contentId as string);
  const snackbar = useSnackbar();

  const { data, isFetched } = useGetContent(contentId, shareToken);
  const { blocks, updateBlock, removeBlock } = usePageBuilderStore();

  const isMetricsCollaborationFFEnbled = useFeature(
    FEATURE_FLAGS.foamMetricsCollaborations,
  ).isEnabled;

  const content = data?.data[0];
  const isContentUnavailable = isOpen && isFetched && !content;
  const isSharedView = Boolean(shareToken);

  const handleRemoveFromMediaKit = () => {
    const block = blocks.find(
      (b) =>
        b.component === 'TalentContent' &&
        (b.properties as TalentContentProperties).customContent.some(
          (c) => c.id === contentId,
        ),
    );

    if (block) {
      const remaining = (
        block.properties as TalentContentProperties
      ).customContent.filter((c) => c.id !== contentId);

      if (remaining.length === 0) {
        removeBlock(block.id);
      } else {
        updateBlock(block.id, {
          ...block,
          properties: {
            ...(block.properties as TalentContentProperties),
            customContent: remaining,
          },
        } as AnyBlock);
      }
    }

    onClose();
    toast('Content removed from media kit', {
      variant: 'success',
      duration: 4000,
    });
  };

  const { onCopy } = useClipboard(content?.shareLink ?? '');

  const hasNavigation = Boolean(onNavigation);

  const trackIds = useMemo(
    () => ({
      ...CONTENT_DETAIL_MODAL_DEFAULT_TRACK_ID,
      ...overrideDefaultTrackIds,
    }),
    [overrideDefaultTrackIds],
  );

  const handleNext = () => onNavigation && onNavigation('next');
  const handlePrevious = () => onNavigation && onNavigation('previous');

  const handleShare = useCallback(() => {
    onCopy();
    snackbar.success({
      description: 'Link successfully copied',
    });
  }, [onCopy, snackbar]);

  const getMetrics = useCallback(() => {
    const actualPlatform = content?.platform;

    if (actualPlatform === SOCIAL_PLATFORMS.instagram) {
      const instagramContent = content as IApiSearchInstagramContent;
      const { viewsCount } = instagramContent;

      const contentContainsViews =
        Object.keys(instagramContent).some((key) => key === 'viewsCount') &&
        Boolean(viewsCount);

      let metrics = PLATFORM_METRICS[instagramContent.channel];

      if (contentContainsViews) {
        if (
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.story ||
          (content as IApiSearchInstagramContent).channel ===
            INSTAGRAM_CONTENT_TYPES.post
        ) {
          const { impressions: _impressions, ...rest } = metrics;
          metrics = rest;
        }
      }

      // Always remove igReelsAggregatedAllPlaysCount for Reels to avoid duplicate Views
      if (
        (content as IApiSearchInstagramContent).channel ===
        INSTAGRAM_CONTENT_TYPES.reels
      ) {
        const {
          igReelsAggregatedAllPlaysCount: _igReelsAggregatedAllPlaysCount,
          ...rest
        } = metrics;
        metrics = rest;
      }
      return metrics;
    }

    return PLATFORM_METRICS[actualPlatform as keyof typeof PLATFORM_METRICS];
  }, [content]);

  // Opening a post is a manager action. Every page mounts this modal closed, so
  // tracking on mount counted page loads (and closes, which clear contentId);
  // and a shared media kit's viewers are not managers at all.
  useEffect(() => {
    if (!isOpen || !contentId || isSharedView) return;
    trackEvent(trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.onOpen]);
  }, [isOpen, contentId, isSharedView, trackIds]);

  const isYoutubeContent = content?.platform === SOCIAL_PLATFORMS.youtube;

  if (isContentUnavailable) {
    return isSharedView ? (
      <NewConfirmationModal
        isOpen={isOpen}
        onCancel={onClose}
        title="Content no longer available"
        paragraph="This content is no longer available - it was removed by the talent from their platform. Contact the agency manager for an updated media kit."
      />
    ) : (
      <NewConfirmationModal
        isOpen={isOpen}
        onConfirm={handleRemoveFromMediaKit}
        onCancel={onClose}
        title="Content no longer available"
        primaryActionLabel="Remove"
        secondaryActionLabel="Cancel"
        paragraph="This content has been removed by the talent from their platform and is no longer available. Remove it from the media kit to ensure shared links stay up to date."
      />
    );
  }

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      track-id={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.modal]}
    >
      <ModalHeaderDefault
        title={
          <Header
            hasNavigation={hasNavigation}
            onNext={handleNext}
            onPrevious={handlePrevious}
            creator={content?.creator as ISearchContentCreator}
            trackIds={trackIds}
          />
        }
      />
      <ModalBody>
        {content ? (
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            gap={{ base: 'spacing-300', md: '0px' }}
          >
            <Flex
              minWidth={
                isYoutubeContent
                  ? { base: 'auto', md: '50%' }
                  : { base: 'auto', md: '280px' }
              }
              paddingRight={{ base: '0', md: '4' }}
              alignItems={'center'}
              flexDir={'column'}
            >
              <Box width={{ base: '70%', md: '100%' }}>
                <Asset
                  content={content}
                  trackId={trackIds[CONTENT_DETAIL_MODAL_TRACK_ID.preview]}
                />
                <AssetDescription content={content} trackIds={trackIds} />
                {(content as IApiSearchInstagramContent).channel ===
                  INSTAGRAM_CONTENT_TYPES.reels && (
                  <div className="mt-3 flex items-start gap-1">
                    <Icon
                      name="Info"
                      className="mt-1 size-icon-md shrink-0 text-neutral-400"
                    />
                    <p className="text-14 text-neutral-400">
                      Note: Some Instagram Reels are subject to Instagram&apos;s
                      visibility and testing rules (e.g., trial Reels). Opening
                      them on Instagram may redirect or hide the content.{' '}
                      <a
                        href="https://creators.instagram.com/blog/instagram-trial-reels"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 no-underline hover:text-blue-700 hover:underline"
                      >
                        Learn more
                      </a>
                    </p>
                  </div>
                )}
              </Box>
            </Flex>
            <Box
              minWidth={
                isYoutubeContent
                  ? { base: 'auto', md: '50%' }
                  : { base: 'auto', md: '448px' }
              }
            >
              <ContentDetailMetrics
                marginBottom={2}
                contentData={content}
                metrics={getMetrics()}
                isMetricsCollaborationFFEnbled={isMetricsCollaborationFFEnbled}
              />
              <Actions
                creator={{
                  fullName: content.creator.fullName as string,
                  avatar: content.creator.avatar as string,
                  status: content.creator.status,
                }}
                content={content}
                onShare={handleShare}
                trackIds={trackIds}
              />
              <ContentCaption content={content} />
            </Box>
          </Flex>
        ) : null}
      </ModalBody>
    </Modal>
  );
};
